import { useState, useCallback, useMemo } from 'react'
import { useMutation } from '@tanstack/react-query'
import { saveRemakeEdits } from '@/lib/api/amazon-listing'
import type { RemakeJob, UserEdits } from '@/lib/api/amazon-listing.types'

export function useRemakeEdits(remakeJob: RemakeJob | undefined) {
  const [edits, setEdits] = useState<UserEdits>({})

  const updateField = useCallback((vi: number, field: string, value: string | string[]) => {
    setEdits((prev) => ({ ...prev, [vi]: { ...prev[String(vi)], [field]: value } }))
  }, [])

  const resetField = useCallback((vi: number, field: string) => {
    setEdits((prev) => {
      const { [field]: _drop, ...rest } = (prev[String(vi)] ?? {}) as Record<string, unknown>
      return { ...prev, [vi]: rest }
    })
  }, [])

  const resetAll = useCallback(() => setEdits({}), [])

  const isDirty = useMemo(
    () => Object.values(edits).some((v) => Object.keys(v).length > 0),
    [edits],
  )

  const { mutate: save } = useMutation({
    mutationFn: () => saveRemakeEdits(remakeJob!.audit_job_id, remakeJob!.id, edits),
  })

  const getDisplayValue = useCallback((vi: number, field: string) => {
    const edited = edits[String(vi)]?.[field as keyof typeof edits[string]]
    if (edited != null) return edited
    const v = remakeJob?.text_result?.variants[vi]
    return v ? (v as unknown as Record<string, unknown>)[field] : undefined
  }, [edits, remakeJob])

  return { edits, updateField, resetField, resetAll, isDirty, save, getDisplayValue }
}
